import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    IVisualArtifactRefSnapshot,
    VisualArtifactRef
} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualArtifactRef';
import {
    DesignAssetsState,
    IDesignAssetsStateSnapshot
} from '@mintdata-wsp/common/state/ui/visualDesigner/assets/DesignAssetsState';
import {BindingState, IBindingsSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/BindingState';
import {GuideProps} from '@mintdata-wsp/common/state/ui/visualDesigner/GuideState';
import {AnySheet, AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {
    ISpreadsheetsStateSnapshot,
    SpreadsheetsState
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetsState';
import {ISurfaceStateSnapshot, SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {IJsonPatch, JsonPatchOp} from '@mintdata-wsp/thirdparty/jiff/lib/jsonPatch';
import {observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export type DesignerVersion = number;
export const DESIGNER_VERSION:DesignerVersion = 62; // NOTE: Bump in case of any broken changes in designer and write an appropriate migration

export interface IDesignStateSnapshot
{
    designerVersion:DesignerVersion;
    surface:ISurfaceStateSnapshot;
    spreadsheets:ISpreadsheetsStateSnapshot;
    bindings:IBindingsSnapshot;
    assets:IDesignAssetsStateSnapshot;
    visualArtifactsRefs:Array<IVisualArtifactRefSnapshot>;
    guideProps?:GuideProps;
}

export class DesignStateFromJsonCtx
{
    public addedSheets:AnySheet[] = [];
    public removedSheets:AnySheet[] = [];
    public addedSlots:AnySlot[] = [];
    public removedSlots:AnySlot[] = [];
}

export class DesignState
{
    public designerVersion:DesignerVersion = DESIGNER_VERSION;

    @observable
    public surface:SurfaceState = null;

    @observable.ref
    public surfaceSnapshot:ISurfaceStateSnapshot = null;

    @observable
    public spreadsheets:SpreadsheetsState = null;

    @observable
    public binding:BindingState = null;

    @observable
    public assets:DesignAssetsState = null;

    public visualArtifactsRefs:Array<VisualArtifactRef> = [];

    public savePromise:Promise<boolean> = null;
    public hasSkippedSaves:boolean = false;

    private constructor()
    {
    }

    public static create():DesignState
    {
        const designState:DesignState = new DesignState();

        designState.surface = SurfaceState.create();
        designState.spreadsheets = SpreadsheetsState.create();
        designState.binding = BindingState.create();
        designState.assets = DesignAssetsState.create();

        return designState;
    }

    public static toJson:ITransformer<DesignState, IDesignStateSnapshot> = createTransformer((designState:DesignState) =>
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        const isNoSurfaceInSampleDesign = sampleDesign && !sampleDesign.showSurface;

        return {
            designerVersion: designState.designerVersion,
            surface: designState.surface && !isNoSurfaceInSampleDesign ? SurfaceState.toJson(designState.surface) : null,
            spreadsheets: designState.spreadsheets ? SpreadsheetsState.toJson(designState.spreadsheets) : null,
            bindings: designState.binding ? BindingState.toJson(designState.binding) : null,
            assets: designState.assets ? DesignAssetsState.toJson(designState.assets) : null,
            visualArtifactsRefs: designState.visualArtifactsRefs
                ? designState.visualArtifactsRefs.map(
                    (visualArtifactRef:VisualArtifactRef) => VisualArtifactRef.toJson(visualArtifactRef)
                )
                : []
        };
    });

    public static fromJson(json:IDesignStateSnapshot,
                           currentState?:DesignState,
                           currentJson?:IDesignStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):DesignState
    {
        if( !currentState )
            return DesignState.fromJsonDeep(json);

        if( !currentJson )
            currentJson = DesignState.toJson(currentState);

        const designState:DesignState = new DesignState();

        designState.designerVersion = json.designerVersion;

        if( !json.surface )
            designState.surface = null;
        else
            designState.surface = json.surface === currentJson.surface
                ? currentState.surface
                : SurfaceState.fromJson(json.surface, currentState.surface, currentJson.surface, ctx);

        designState.spreadsheets = json.spreadsheets === currentJson.spreadsheets
            ? currentState.spreadsheets
            : SpreadsheetsState.fromJson(json.spreadsheets, currentState.spreadsheets, currentJson.spreadsheets, ctx);

        designState.binding = json.bindings === currentJson.bindings
            ? currentState.binding
            : BindingState.fromJson(
                json.bindings,
                designState.spreadsheets,
                designState.surface,
                currentState.binding,
                currentJson.bindings
            );

        designState.assets = json.assets === currentJson.assets
            ? currentState.assets
            : DesignAssetsState.fromJson(
                json.assets,
                currentState.assets,
                currentJson.assets,
                ctx
            );

        designState.visualArtifactsRefs = json.visualArtifactsRefs.map((_, index:number) =>
        {
            return json.visualArtifactsRefs[index] === currentJson.visualArtifactsRefs[index]
                ? currentState.visualArtifactsRefs[index]
                : VisualArtifactRef.fromJson(json.visualArtifactsRefs[index]);
        });

        return designState;
    }

    public static fromJsonDeep(json:IDesignStateSnapshot):DesignState
    {
        const designState:DesignState = new DesignState();

        designState.designerVersion = json.designerVersion;
        designState.spreadsheets = json.spreadsheets ? SpreadsheetsState.fromJsonDeep(json.spreadsheets) : null;

        designState.surface = json.surface ? SurfaceState.fromJsonDeep(json.surface) : null;
        designState.binding = json.surface && json.bindings
            ? BindingState.fromJsonDeep(json.bindings, designState.spreadsheets, designState.surface)
            : BindingState.create();

        designState.assets = json.assets ? DesignAssetsState.fromJsonDeep(json.assets) : null;

        designState.visualArtifactsRefs = json.visualArtifactsRefs
            ? json.visualArtifactsRefs.map(VisualArtifactRef.fromJsonDeep)
            : [];

        return designState;
    }

    public static getInitialPatches():IJsonPatch[]
    {
        const designState:DesignState = DesignState.create();
        const json:IDesignStateSnapshot = DesignState.toJson(designState);

        return Object.keys(json).map((key) => ({
            op: JsonPatchOp.Add,
            path: `/${key}`,
            value: json[key]
        }));
    }
}
